import cx from 'classnames';
import { useState } from 'react';

import styles from './Header.module.css';

import { ReactComponent as ArrowLeftCircleSvg } from '@frontend/icons/arrow-left-circle.svg';
import {
  Center,
  Container,
  Hamburger,
  Icon,
  MobileNavMenu,
  Text,
  UnstyledButton,
} from '@frontend/ui-library';
import { useWidgetContext } from '../../context/WidgetContext';
import ClosePayment from '../ClosePayment';
import ExpirationBar from '../ExpirationBar/ExpirationBar';
import { isOfframpBill } from '../../utils';

interface HeaderProps {
  className?: string;
}

export default function Header({ className }: HeaderProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    id,
    merchantName,
    allowClosePayment,
    expiresAt,
    billType,
    onMobileBackButtonClick,
  } = useWidgetContext();

  const shouldShowCancelButton = billType !== 'merchant' && allowClosePayment;
  const isOffRamp = isOfframpBill(billType);
  const idLabel = isOffRamp ? 'Off ramp ID' : 'Payment ID';

  return (
    <header className={cx(styles['base'], className)}>
      <Container>
        <div className={styles['main-bar']}>
          <div className={styles['left-column']}>
            <Text
              className={styles['payment-id']}
              size="tiny-callout"
              color="royal-100"
            >
              {idLabel}: <br />
              {id}
            </Text>
            {onMobileBackButtonClick && (
              <UnstyledButton
                onClick={onMobileBackButtonClick}
                className={styles['mobile-back-button']}
              >
                <Icon
                  size={24}
                  component={<ArrowLeftCircleSvg />}
                  color="white"
                />
              </UnstyledButton>
            )}
          </div>
          <div className={styles['middle-column']}>
            <div className={styles['merchant-name']}>{merchantName}</div>
            {typeof expiresAt === 'number' && (
              <ExpirationBar
                className={styles['expiration-display-mobile']}
                chipVariant
                expiresAt={expiresAt * 1000}
                isOffRamp={isOffRamp}
              />
            )}
          </div>
          <div className={styles['right-column']}>
            {shouldShowCancelButton && (
              <ClosePayment
                className={styles['close-payment']}
                isOffRamp={isOffRamp}
              />
            )}
            <Hamburger
              className={styles['hamburger']}
              onClick={() => setMenuOpen(true)}
            />
          </div>
        </div>
      </Container>
      {typeof expiresAt === 'number' && (
        <ExpirationBar
          className={styles['expiration-display-desktop']}
          expiresAt={expiresAt * 1000}
          isOffRamp={isOffRamp}
        />
      )}
      {menuOpen && (
        <MobileNavMenu onRequestClose={() => setMenuOpen(false)}>
          <Center direction="column">
            {!!id && (
              <Text
                as="div"
                transform="uppercase"
                size="tiny-callout"
                color="royal-100"
                align="center"
              >
                {idLabel}: <br /> {id}
              </Text>
            )}
            {allowClosePayment && (
              <ClosePayment isOffRamp={isOffRamp} mt="24" />
            )}
          </Center>
        </MobileNavMenu>
      )}
    </header>
  );
}
